/**
 * Конфигурация wizard'а онбординга отеля — 8 шагов (дизайн v4, группа 3).
 *
 * Один источник правды: список шагов + функция валидации перехода. Это
 * позволяет тестировать навигацию (вперёд/назад, блокировку перехода при
 * невалидном шаге) изолированно от React-рендера.
 */

export type WizardStepId =
  | 'manager'        // Шаг 1: приглашение управляющего
  | 'hotel'          // Шаг 2: данные отеля
  | 'tier'           // Шаг 3: тариф + capabilities
  | 'branding'       // Шаг 4: брендинг
  | 'integrations'   // Шаг 5: интеграции
  | 'seed'           // Шаг 6: стартовые данные
  | 'billing'        // Шаг 7: проверка биллинга
  | 'golive';        // Шаг 8: запуск

export interface WizardStepMeta {
  id: WizardStepId;
  index: number; // 1-based для отображения «Шаг N из 8»
  title: string;
}

export const WIZARD_STEPS: ReadonlyArray<WizardStepMeta> = [
  { id: 'manager', index: 1, title: 'Приглашение управляющего' },
  { id: 'hotel', index: 2, title: 'Данные отеля' },
  { id: 'tier', index: 3, title: 'Тариф и возможности' },
  { id: 'branding', index: 4, title: 'Брендинг' },
  { id: 'integrations', index: 5, title: 'Интеграции' },
  { id: 'seed', index: 6, title: 'Стартовые данные' },
  { id: 'billing', index: 7, title: 'Проверка биллинга' },
  { id: 'golive', index: 8, title: 'Запуск' },
] as const;

export const WIZARD_TOTAL_STEPS = WIZARD_STEPS.length;

/** Данные, собираемые на клиенте по ходу wizard'а. */
export interface WizardData {
  hotelName: string;
  managerEmail: string;
  managerFirstName: string;
  managerLastName: string;
  welcomeMessage: string;
  slug: string;
  region: string;
  legalEntity: string;
  inn: string;
  tier: string;
  capabilities: string[];
}

export const EMPTY_WIZARD_DATA: WizardData = {
  hotelName: '',
  managerEmail: '',
  managerFirstName: '',
  managerLastName: '',
  welcomeMessage: '',
  slug: '',
  region: '',
  legalEntity: '',
  inn: '',
  tier: 'starter',
  capabilities: [],
};

/** 12 переключателей capability (дизайн v4, шаг 3). */
export const CAPABILITY_OPTIONS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'inventory', label: 'Управление номерным фондом' },
  { key: 'pms_bnovo', label: 'Интеграция Bnovo PMS' },
  { key: 'pms_travelline', label: 'Интеграция TravelLine' },
  { key: 'sms', label: 'SMS-уведомления' },
  { key: 'vendor_module', label: 'Модуль поставщиков' },
  { key: 'analytics', label: 'Аналитика' },
  { key: 'white_label', label: 'White-label' },
  { key: 'multi_property', label: 'Мультиобъектность' },
  { key: 'events', label: 'События / афиша' },
  { key: 'reviews', label: 'Отзывы гостей' },
  { key: 'payments', label: 'Онлайн-оплата (T-Bank)' },
  { key: 'api_access', label: 'Доступ к API' },
] as const;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SLUG_RE = /^[a-z][a-z0-9-]{1,30}$/;
const INN_RE = /^\d{10}(\d{2})?$/;

/**
 * Проверка возможности перехода ВПЕРЁД с указанного шага.
 * Назад можно всегда (валидация не требуется).
 */
export function canAdvanceFrom(step: WizardStepId, data: WizardData): boolean {
  switch (step) {
    case 'manager':
      return (
        data.hotelName.trim().length > 0 &&
        EMAIL_RE.test(data.managerEmail) &&
        data.managerFirstName.trim().length > 0 &&
        data.managerLastName.trim().length > 0
      );
    case 'hotel':
      return (
        SLUG_RE.test(data.slug) &&
        data.region.trim().length > 0 &&
        data.legalEntity.trim().length > 0 &&
        INN_RE.test(data.inn)
      );
    case 'tier':
      return data.tier.length > 0;
    // Шаги 4–7 в каркасе опциональны (брендинг/интеграции/seed/биллинг).
    case 'branding':
    case 'integrations':
    case 'seed':
    case 'billing':
      return true;
    case 'golive':
      return true; // последний шаг — переход = submit
    default:
      return false;
  }
}

/** Индекс следующего шага или null, если текущий — последний. */
export function nextStepIndex(currentIndex: number): number | null {
  return currentIndex < WIZARD_TOTAL_STEPS ? currentIndex + 1 : null;
}

/** Индекс предыдущего шага или null, если текущий — первый. */
export function prevStepIndex(currentIndex: number): number | null {
  return currentIndex > 1 ? currentIndex - 1 : null;
}
